import { Trans, useLingui } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, object, shape, string } from 'prop-types'

import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import IconCircleInfo from 'views/components/atoms/Icon/IconCircleInfo'
import IconTick from 'views/components/atoms/Icon/IconTick'
import IconTruck from 'views/components/atoms/Icon/IconTruck'
import Price from 'views/components/atoms/Price'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ButtonSmall from 'views/components/molecules/ButtonSmall'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import { DELIVERY_INFO_MODAL_ID } from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

import { getFreeDeliveryTypes } from './helpers'

const BuyBoxDeliveryCosts = ({
  isFreeDelivery,
  prescriptionType,
  useLink,
  isMpOffer,
  offerDeliveryCosts,
  sellerFreeShipping,
  publicRuntimeConfig: {
    publicConfig: {
      currency,
      shippingCostThreshold,
      deliveryCosts: { symbolRight },
    },
    pageProperties: { experiments },
  },
}) => {
  const { i18n } = useLingui()
  const isRebrandingEnabled = isRebrandingITActive(experiments)
  const { freeDeliveryTypes, payedDeliveryTypes } = getFreeDeliveryTypes({
    isFreeDelivery,
  })

  const desktopLabelClass = classNames('u-hidden@small-tiny', {
    'u-padding-x-small--right': !useLink,
  })
  const mobileLabelClass = classNames(
    'u-hidden@medium u-color--greyLight-dark o-BuyBoxDeliveryCosts__mobile-label',
    {
      'u-margin-xx-small--right': !useLink,
    },
    {
      'o-BuyBoxFreeShippingTextAlign': isRebrandingEnabled,
    }
  )

  const renderSaeOfferBuyBoxDeliveryCosts = () => {
    const isRebrandingEnabled = isRebrandingITActive(experiments)
    const priceRendered = shippingCostThreshold ? (
      <Price
        amount={shippingCostThreshold}
        currency={currency}
        decimalPlaces={isRebrandingEnabled ? 2 : 0}
        symbolRight={symbolRight}
      />
    ) : null

    const label = useLink ? (
      <Trans
        id="buyBox.link.label.belowFreeShippingLimitWithLink"
        values={{ amount: priceRendered }}
        components={{
          0: (
            <ButtonAsLink
              underlined
              inheritColor
              className={classNames({
                'o-BuyBoxFreeShippingTextColor': isRebrandingEnabled,
              })}
              data-a11y-dialog-show={DELIVERY_INFO_MODAL_ID}
              type="button"
            />
          ),
        }}
      />
    ) : (
      <Trans
        id="buyBox.link.label.belowFreeShippingLimit"
        values={{ amount: priceRendered }}
      />
    )

    return (
      <div
        className="o-BuyBoxDeliveryCosts l-flex"
        data-prescription-type-switch
      >
        {isRebrandingEnabled &&
          (isFreeDelivery(prescriptionType) ? (
            <IconTick size={20} className="u-color--positive" />
          ) : (
            <IconTruck />
          ))}
        <p
          className={classNames(
            'u-color--positive u-font-weight--bold u-no-margin u-text-align--right@small-tiny o-BuyBoxFreeShippingText',
            { 'o-BuyBoxDeliveryCosts__text': !isRebrandingEnabled }
          )}
          data-prescription-types={freeDeliveryTypes}
          aria-hidden={isFreeDelivery(prescriptionType) ? null : true}
        >
          <Trans id="buyBox.link.label.aboveFreeShippingLimit" />
        </p>
        <p
          className="u-no-margin l-flex u-text-align--right@small-tiny"
          data-prescription-types={payedDeliveryTypes}
          aria-hidden={isFreeDelivery(prescriptionType) ? true : null}
        >
          <span className={mobileLabelClass}>{label}</span>
          <span className={desktopLabelClass}>{label}</span>
          {!useLink && (
            <ModalShowButton
              component={ButtonSmall}
              name="buybox_delivery_info"
              modalId={DELIVERY_INFO_MODAL_ID}
              title={i18n._('buyBox.link.label.freeShipping')}
              inline
              primary
              small
              noRestricted
            >
              <span className="u-visuallyhidden">
                <Trans id="buyBox.link.label.freeShipping" />
              </span>
              <IconCircleInfo size="small" />
            </ModalShowButton>
          )}
        </p>
      </div>
    )
  }
  const renderMpOfferBuyBoxDeliveryCosts = () => {
    const isItemZero = item => item.totalShippingPrice === 0

    let cheapestDeliveryCost = 0,
      areAllMethodsZero,
      isThereZeroValueAmongAllCosts

    isThereZeroValueAmongAllCosts =
      offerDeliveryCosts.length > 1 &&
      offerDeliveryCosts.some(odc => odc.totalShippingPrice === 0)

    if (offerDeliveryCosts.length > 0) {
      areAllMethodsZero = offerDeliveryCosts.every(isItemZero)

      cheapestDeliveryCost = offerDeliveryCosts[0].totalShippingPrice
    }

    let shouldShowDeliveryCostsModal

    if (
      (offerDeliveryCosts.length > 0 && !sellerFreeShipping) ||
      isThereZeroValueAmongAllCosts
    ) {
      if (offerDeliveryCosts.length === 1 && areAllMethodsZero) {
        shouldShowDeliveryCostsModal = false
      } else {
        shouldShowDeliveryCostsModal = true
      }
    }

    const priceRendered = (
      <Price
        amount={cheapestDeliveryCost}
        decimalPlaces={2}
        symbolRight={symbolRight}
      />
    )

    const label = shouldShowDeliveryCostsModal ? (
      <Trans
        id="buyBox.link.label.belowFreeShippingLimitWithLinkMp"
        values={{ amount: priceRendered }}
        components={{
          0: (
            <ButtonAsLink
              underlined
              data-a11y-dialog-show={DELIVERY_INFO_MODAL_ID}
              type="button"
            />
          ),
        }}
      />
    ) : (
      <p
        className="
          u-color--positive
          u-font-weight--bold
          u-no-margin
          u-text-align--right@small-tiny
          o-BuyBoxDeliveryCosts__text
        "
      >
        <Trans id="buyBox.link.label.aboveFreeShippingLimit" />
      </p>
    )

    return (
      <div
        className="o-BuyBoxDeliveryCosts l-flex"
        data-prescription-type-switch
      >
        <span className={mobileLabelClass}>{label}</span>
        <span className={desktopLabelClass}>{label}</span>
      </div>
    )
  }

  return isMpOffer
    ? renderMpOfferBuyBoxDeliveryCosts()
    : renderSaeOfferBuyBoxDeliveryCosts()
}

BuyBoxDeliveryCosts.propTypes = {
  isFreeDelivery: func.isRequired,
  prescriptionType: string,
  publicRuntimeConfig: shape({}),
  useLink: bool,
  isMpOffer: bool,
  offerDeliveryCosts: arrayOf(object),
  sellerFreeShipping: bool,
}

BuyBoxDeliveryCosts.defaultProps = {
  prescriptionType: null,
  publicRuntimeConfig: {},
  useLink: false,
  isMpOffer: false,
  offerDeliveryCosts: [],
  sellerFreeShipping: false,
}

export default withShopAndRuntimeConfig(BuyBoxDeliveryCosts)
